<template>
  <section
    id="icon-pack-icons"
    class="padding-top-5xl"
    style="--button-hover-background: var(--theme-button-hover-background); --button-hover-border-color: var(--theme-button-hover-border-color)"
  >
    <div class="container">
      <div class="row roomy align-between align-top margin-bottom-4xl">
        <div
          class="column display-flex tablet:flex-items-center"
          style="gap: var(--spacing-xl)"
        >
          <form
            class="icons-search form"
            method="post"
            name="header_search"
            @submit.prevent="search()"
          >
            <div
              class="with-icon-before"
              style="--input-padding: var(--spacing-sm) var(--spacing-xl) var(--spacing-xs) var(--spacing-xl)"
            >
              <font-awesome-icon
                :icon="$v6.solid.faSearch"
                style="line-height: 1; top: 13px"
              />
              <label
                for="search_query"
                class="sr-only"
              >
                Search {{ iconPack().label }} Icons
              </label>

              <input
                id="search_query"
                ref="searchQuery"
                :placeholder="`Search ${iconPack().label} icons...`"
                class="rounded margin-bottom-0"
                name="search_query"
                type="text"
                value=""
                @focus="searchFieldFocus = true"
                @blur="searchFieldFocus = false"
              />
            </div>
            <button
              class="sr-only"
              type="submit"
            >
              Search {{ iconPack().label }} Icons
            </button>
          </form>
          <button
            :aria-label="`Shuffle ${iconPack().label}'s Icons`"
            class="button flat with-border margin-bottom-0 display-none tablet:display-inline-block"
            data-balloon-pos="up"
            style="--border-color: var(--fa-gravy); --button-background: transparent; --button-color: var(--fa-navy)"
            @click.prevent="shuffleIcons()"
          >
            <font-awesome-icon
              :icon="$v6.solid.faShuffle"
              size="lg"
            />
          </button>
        </div>
        <div class="column text-right">
          <site-link
            :to="$search({ iconPacks: iconPack().name })"
            class="button flat with-border margin-bottom-0"
            style="--border-color: var(--fa-gravy); --button-background: transparent; --button-color: var(--fa-navy)"
            @click.native="$posthog.capture('pack_landing:all_packs_link_click', { pack: iconPack().name })"
          >
            View All
            <span class="display-none tablet:display-inline">
              {{ iconPack().label }} Icons
              <font-awesome-icon
                :icon="$v6.solid.faGrid2"
                fixed-width
                size="lg"
                class="margin-left-md"
              />
            </span>
          </site-link>
        </div>
      </div>

      <pack-icon-list
        v-for="(familyStyle, idx) in familyStyles"
        :ref="`iconList${idx}`"
        :key="idx"
        :index="algoliaIndex"
        :is-last="idx === familyStyles.length - 1"
        :is-pro-user="isProUser"
        :primary-color="iconPack().theme.primary"
        :search-field-focus="false"
        :selected-family="familyStyle.family"
        :selected-icon-id="`0`"
        :selected-style="familyStyle.style"
        :version="version"
        :visual-tags="familyStyle.visualTags"
        @toggleIcon="toggleIcon"
        @closeIcon="closeIcon"
      />
    </div>

    <div
      id="iconPreview"
      class="wrap-icon-detail"
    >
      <transition
        name="toggle-icon-detail"
        appear
        enter-active-class="animate__animated animate__faster animate__fadeIn"
        @after-leave="ejectIconPreview"
      >
        <modal-icon-detail
          ref="iconModal"
          :default-styles="defaultStyles"
          :icon-abstract="iconAbstract"
          :icon-name="selectedIconId"
          :icon="icon"
          :index="algoliaIndex"
          :is-duotone="isDuotoneIcon"
          :is-pro-user="isProUser"
          :is-showing="isShowingIconModal"
          :selected-from-search="selectedIcon"
          :styling="styling"
          :viggo="viggo"
          @closeIcon="closeIcon"
          @nextIcon="nextIcon"
          @prevIcon="prevIcon"
          @resetStyles="resetStyles"
          @stylingChange="stylingChange"
        />
      </transition>
    </div>
  </section>
</template>

<script>
  import { DEFAULT_STYLING } from 'app/views/icons/defaults'
  import { loadViggo } from 'app/viggo'
  import { fetchIndexName } from 'app/views/search/utils'
  import { EventTrackingMixin, SubscriptionMixin } from 'app/mixins'
  import PackIconList from './PackIconList'
  import ModalIconDetail from 'app/views/icons/components/icons/details/ModalIconDetail'
  import { isDuotoneFamily } from 'app/releases.js'

  export default {
    name: 'PackIcons',

    components: {
      PackIconList,
      ModalIconDetail
    },

    mixins: [EventTrackingMixin, SubscriptionMixin],

    inject: ['iconPack'],

    data() {
      return {
        activeIconList: null,
        icon: null,
        iconAbstract: null,
        isLoading: false,
        isShowingIconModal: false,
        selectedIcon: null,
        selectedIconId: null,
        styling: {
          animation: DEFAULT_STYLING.animation,
          colorPrimary: DEFAULT_STYLING.colorPrimary,
          colorSecondary: DEFAULT_STYLING.colorSecondary,
          family: 'classic',
          opacityPrimary: DEFAULT_STYLING.opacityPrimary,
          opacitySecondary: DEFAULT_STYLING.opacitySecondary,
          rotation: DEFAULT_STYLING.rotation,
          size: DEFAULT_STYLING.size,
          style: 'solid'
        },
        viggo: null
      }
    },

    computed: {
      algoliaIndex() {
        return fetchIndexName(this.$releases.active.version)
      },

      defaultStyles() {
        const stylingDefaults = { ...DEFAULT_STYLING }

        stylingDefaults.family = this.styling.family
        stylingDefaults.style = this.styling.style

        return stylingDefaults
      },

      familyStyles() {
        return this.iconPack().familyStyles
      },

      isDuotoneIcon() {
        return isDuotoneFamily(this.styling?.family)
      },

      isProUser() {
        return this.marketing.hasActiveProSubscription
      },

      version() {
        return this.$releases.active.version
      }
    },

    destroyed() {
      window.removeEventListener('popstate', this.closeIcon)
      document.removeEventListener('keydown', this.keydownEvent)
    },

    async created() {
      window.addEventListener('popstate', this.closeIcon)
      document.addEventListener('keydown', this.keydownEvent)

      this.isLoading = true
      if (!this.viggo) {
        try {
          this.viggo = await loadViggo()
        } catch (error) {
          throw new Error('Error loading viggo', error)
        }
      }

      this.isLoading = false
    },

    methods: {
      closeIcon() {
        if (this.selectedIconId && this.styling.family && this.styling.style) {
          this.selectedIcon = null
          this.selectedIconId = null
          this.isShowingIconModal = false
          this.styling = { ...this.styling, ...DEFAULT_STYLING, family: null, style: null }
        }
      },

      ejectIconPreview() {
        const iconPreview = document.getElementById('iconPreview')
        if (iconPreview) {
          document.getElementById('searchContent').insertAdjacentElement('afterend', iconPreview)
        }
      },

      keydownEvent(event) {
        if (this.isShowingIconModal) {
          switch (event.key) {
            case 'ArrowLeft':
              this.prevIcon()
              break
            case 'ArrowRight':
              this.nextIcon()
              break
            case 'Escape':
              this.closeIcon()
              break
          }
        }
      },

      nextIcon() {
        this.activeIconList?.$refs.iconList?.next()
      },

      prevIcon() {
        this.activeIconList?.$refs.iconList?.previous()
      },

      resetStyles() {
        this.styling = { ...this.styling, ...DEFAULT_STYLING }
      },

      search() {
        const query = this.$refs.searchQuery.value
        if (query) {
          this.$search({ query })
        }
      },

      shuffleIcons() {
        this.$search({ query: '', page: 1 })
      },

      stylingChange(styling) {
        this.styling = { ...this.styling, ...styling }
      },

      toggleIcon(icon, elementTag) {
        this.selectedIcon = icon
        this.selectedIconId = elementTag
        this.isShowingIconModal = true

        // For brand icons, use the pack's defined family/style combination
        if (icon.family === 'brands' || icon.style === 'brands') {
          this.icon = icon
          // Use the first familyStyle from the pack's icons array
          const brandFamilyStyle = this.iconPack().icons[0]
          this.styling = { ...this.styling, family: brandFamilyStyle.family, style: brandFamilyStyle.style }
        } else {
          this.icon = icon
          this.styling = { ...this.styling, family: icon.family, style: icon.style }
        }

        // For brand icons, always use the first icon list since there's only one family/style
        const index =
          icon.family === 'brands' || icon.style === 'brands' ? 0 : this.familyStyles.findIndex((fs) => fs.family === icon.family && fs.style === icon.style)

        if (index !== -1) {
          this.activeIconList = this.$refs[`iconList${index}`][0]
        } else {
          this.activeIconList = null
          console.warn(`No matching family/style found for family: ${icon.family}, style: ${icon.style}`)
        }
      }
    }
  }
</script>
